import { listen } from "@tauri-apps/api/event";
import { create } from "zustand";
import { getMyWindowLabel } from "@/lib/owner-state-client";
import {
  emitWorkspaceModeEvent,
  WORKSPACE_MODE_CLAIMED_EVENT,
  WORKSPACE_MODE_QUERY_EVENT,
  WORKSPACE_MODE_RELEASED_EVENT,
  type WorkspaceModeEventPayload,
} from "@/lib/workspace-mode-events";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";
import { useDesktopMode } from "./use-desktop-mode";
import { getWindowDisplayId } from "./use-window-role";

/** Window label of another display that shows the Workspace, if any. */
export const useWorkspaceHolder = create<{ otherDisplayHolderLabel: string | null }>(
  () => ({ otherDisplayHolderLabel: null }),
);

const setOtherDisplayHolder = (label: string | null) =>
  useWorkspaceHolder.setState({ otherDisplayHolderLabel: label });

/** Another display's window shows the Workspace over the shared canvas. */
export function workspaceOpenOnOtherDisplay(): boolean {
  return useWorkspaceHolder.getState().otherDisplayHolderLabel !== null;
}

function isFromOtherDisplay({ displayId, windowLabel }: WorkspaceModeEventPayload) {
  if (windowLabel === getMyWindowLabel()) return false;
  const myDisplayId = getWindowDisplayId();
  return displayId === null || myDisplayId === null || displayId !== myDisplayId;
}

/** Yield Workspace to a claim from another display and remember who holds it.
 *  A window that loads while another holds the Workspace asks for a re-claim. */
export function attachWorkspaceHolder(): () => void {
  let cancelled = false;
  const unlisteners: Array<() => void> = [];
  const track = (promise: Promise<() => void>) =>
    promise.then((unlisten) => {
      if (cancelled) safeTauriUnlisten(unlisten);
      else unlisteners.push(unlisten);
    });
  const claimedReady = track(
    listen<WorkspaceModeEventPayload>(WORKSPACE_MODE_CLAIMED_EVENT, ({ payload }) => {
      if (!isFromOtherDisplay(payload)) {
        if (payload.windowLabel === getMyWindowLabel()) setOtherDisplayHolder(null);
        return;
      }
      setOtherDisplayHolder(payload.windowLabel);
      if (useDesktopMode.getState().mode === "denk") {
        useDesktopMode.getState().closeWorkspace();
      }
    }),
  );
  const releasedReady = track(
    listen<WorkspaceModeEventPayload>(WORKSPACE_MODE_RELEASED_EVENT, ({ payload }) => {
      if (payload.windowLabel === useWorkspaceHolder.getState().otherDisplayHolderLabel) {
        setOtherDisplayHolder(null);
      }
    }),
  );
  track(
    listen<WorkspaceModeEventPayload>(WORKSPACE_MODE_QUERY_EVENT, ({ payload }) => {
      if (payload.windowLabel === getMyWindowLabel()) return;
      if (useDesktopMode.getState().mode === "denk") {
        emitWorkspaceModeEvent(WORKSPACE_MODE_CLAIMED_EVENT);
      }
    }),
  );
  void Promise.all([claimedReady, releasedReady]).then(() => {
    if (!cancelled) emitWorkspaceModeEvent(WORKSPACE_MODE_QUERY_EVENT);
  });
  return () => {
    cancelled = true;
    setOtherDisplayHolder(null);
    unlisteners.forEach(safeTauriUnlisten);
  };
}
